/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import {
  UnespCoreMenuNavigationService,
  UnespCoreMessageService,
  UnespCoreUserService,
  UnespCoreUsuario,
} from 'src/libs/unesp-core'
import { BehaviorSubject, map } from 'rxjs'
import { UsuarioLogin } from 'src/app/models/usuario-login'
import { environment } from 'src/environments/environment'

@Injectable({
  providedIn: 'root',
})
export class UserLoginService {
  private baseUrl: string

  constructor(
    private unespCoreUserService: UnespCoreUserService,
    private http: HttpClient,
    private messageService: UnespCoreMessageService,
    private router: Router,
    private unespCoreMenuNavigationService: UnespCoreMenuNavigationService
  ) {
    this.baseUrl = environment.baseUrlApi
    this.unespCoreUserService.user$.subscribe(user => {
      this._userLogin$.next(user as UsuarioLogin)
    })
  }

  private _userLogin$ = new BehaviorSubject<UsuarioLogin | null>(null)

  get user$(): BehaviorSubject<UsuarioLogin | null> {
    return this._userLogin$
  }

  getUser(): UsuarioLogin | null {
    return this.unespCoreUserService.getUser() as UsuarioLogin
  }

  doLoginToken(token: string): Promise<UnespCoreUsuario> {
    return new Promise((resolve, reject) =>
      this.getUsuario(token).subscribe({
        next: usuario => {
          if (usuario) {
            let user: UnespCoreUsuario = usuario
            user.roles = user.authorities.map(s => s.replace(/^ROLE_FFC.UNESP.INTRANET_/, ''))
            user.roles.push('**')
            user.nomeUsuario = this.removeTrailingDuplicate(user?.nomeUsuario || '')

            this.verificarCtuParaMenu(user).then(() => {
              this.unespCoreUserService.setUser(user)
              resolve(user)
            })
          }
        },
        error: err => {
          this.messageService.showMessageError(err.error.message)
          this.router.navigate(['/'])
          reject(err as Error)
        },
      })
    )
  }

  private verificarCtuParaMenu(user: UnespCoreUsuario): Promise<void> {
    if (user.roles.includes('SUPERADMIN')) return Promise.resolve()
    if (!user.roles.includes('RH')) return Promise.resolve()

    return new Promise(resolve => {
      this.http.get<boolean>(`${this.baseUrl}/gestao-usuario/verificar-acesso`, { withCredentials: true })
        .subscribe({
          next: isCtu => {
            if (isCtu) {
              user.roles.push('CTU_ADMIN')
            }
            resolve()
          },
          error: () => resolve()
        })
    })
  }

  private getUsuario(token: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    })
    let options = { headers: headers, withCredentials: true }
    return this.http.post(`${this.baseUrl}/publico/auth/token`, null, options).pipe<UnespCoreUsuario>(
      map(user => {
        return user as UnespCoreUsuario
      })
    )
  }

  private removeTrailingDuplicate(input: string): string {
    const words = input.trim().split(' ')
    const len = words.length

    for (let i = 1; i <= len / 2; i++) {
      const part2 = words.slice(len - i).join(' ')
      const part1End = words.slice(len - 2 * i, len - i).join(' ')

      if (part2 === part1End) {
        return words.slice(0, len - i).join(' ')
      }
    }

    return input
  }
}
